import { useState, type ReactNode } from 'react';
import { ChevronDown } from 'lucide-react';

interface Props {
  /** Titre du slice (ex: "Qualité (Qualiopi)"). */
  title: string;
  /** Icône à gauche du titre. */
  icon?: ReactNode;
  /** Badge / actions à droite du header (CTA, lien, statut…). */
  badge?: ReactNode;
  /** Résumé toujours visible (KPIs, prochain événement…). */
  summary?: ReactNode;
  /** Détail dépliable. */
  children: ReactNode;
  /** État initial : ouvert ou fermé. */
  defaultOpen?: boolean;
}

/**
 * Pattern slice pliable :
 *   ┌──────────────────────────────────────────────┐
 *   │ 🛡 TITRE ........................ [badge] ⌄ │
 *   │ ────────────────────────────────────────────│
 *   │ [SUMMARY — KPIs toujours visibles]          │
 *   │ ────────────────────────────────────────────│
 *   │ [DÉTAIL — collapsible]                      │
 *   └──────────────────────────────────────────────┘
 *
 * Demandé par Rafik (iter 3, 27/05/2026) : tous les widgets denses
 * doivent permettre une lecture rapide en mode replié et un drill-down
 * sur clic. Voir DOMAINS.md.
 */
export function CollapsibleSlice({
  title,
  icon,
  badge,
  summary,
  children,
  defaultOpen = false,
}: Props) {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className="overflow-hidden rounded-xl border border-neutral-200 bg-white shadow-sm">
      {/* Header cliquable */}
      <button
        type="button"
        onClick={() => setIsOpen((o) => !o)}
        className="flex w-full items-center justify-between gap-3 px-4 py-3 transition-colors hover:bg-neutral-50"
      >
        <div className="flex flex-1 items-center gap-2 text-xs font-semibold uppercase tracking-wider text-neutral-500">
          {icon}
          {title}
        </div>
        <div className="flex items-center gap-2">
          {badge && <div onClick={(e) => e.stopPropagation()}>{badge}</div>}
          <ChevronDown
            className={`h-4 w-4 text-neutral-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          />
        </div>
      </button>

      {/* Résumé (toujours visible) */}
      {summary && <div className="border-t border-neutral-100 px-4 py-3">{summary}</div>}

      {/* Détail (collapsible) */}
      {isOpen && <div className="border-t border-neutral-100 px-4 py-3">{children}</div>}
    </div>
  );
}
